import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { format } from "date-fns";
import { Clock, User, AlertCircle } from "lucide-react";
import { Helmet } from 'react-helmet-async';
import { compile, run } from "@mdx-js/mdx";
import * as runtime from "react/jsx-runtime";
import useFetchBlogDetails from "../hooks/Blog/useFetchBlogDetails";
import { BlogPost } from "../hooks/useBlogPosts";
import Preloader from '../components/ui/Preloader';

// Improved MDX Renderer with Error Boundary and HTML Fallback
function MDXStringRenderer({ mdxString }: { mdxString: string }) {
  const [CompiledMDX, setCompiledMDX] = useState<React.ComponentType | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [useHtmlFallback, setUseHtmlFallback] = useState(false);

  // Helper function to fix common HTML issues
  const fixHtml = (html: string): string => {
    if (!html) return '';
    
    // Fix unclosed <br> tags (convert <br> to <br />)
    html = html.replace(/<br>/gi, '<br />');
    
    // Fix other self-closing tags
    html = html.replace(/<img([^>]*)>/gi, '<img$1 />');
    html = html.replace(/<hr>/gi, '<hr />');
    
    // Fix mismatched tags by ensuring proper nesting
    // This is a simple fix - for complex cases, consider using a proper HTML parser
    return html;
  };

  useEffect(() => {
    const compileMDX = async () => {
      try {
        // First, try to fix common HTML issues
        const fixedContent = fixHtml(mdxString);
        
        // Compile MDX string into JSX code
        const compiled = String(
          await compile(fixedContent, { outputFormat: "function-body" })
        );
        
        const { default: Component } = await run(compiled, { ...runtime });
        setCompiledMDX(() => Component);
        setError(null);
      } catch (compilationError) {
        console.error("Error compiling MDX, falling back to HTML rendering:", compilationError);
        // If MDX compilation fails, fall back to HTML rendering
        setUseHtmlFallback(true);
        setError(null);
      }
    };

    if (mdxString) {
      compileMDX();
    }
  }, [mdxString]);

  // If we're using HTML fallback, render as HTML
  if (useHtmlFallback) {
    return (
      <div 
        className="prose prose-lg prose-blue max-w-none"
        dangerouslySetInnerHTML={{ __html: fixHtml(mdxString) }} 
      />
    );
  }

  // Error handling component (shouldn't show now with fallback)
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 p-4 rounded-lg flex items-center mb-4">
        <AlertCircle className="h-6 w-6 text-red-500 mr-3" />
        <div>
          <h3 className="font-semibold text-red-800">Content Rendering Error</h3>
          <p className="text-red-600 text-sm">{error.message}</p>
        </div>
      </div>
    );
  }

  // Loading state
  if (!CompiledMDX && !useHtmlFallback) {
    return (
      <div className="flex justify-center items-center py-10">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return CompiledMDX ? <CompiledMDX /> : null;
}

export const BlogPostPage = () => {
  const { slug: id } = useParams<{ slug: string }>();
  const { post: reduxPost, status } = useFetchBlogDetails(id || '');

  // Convert Redux post to useBlogPosts post format
  const post: BlogPost | null = reduxPost ? {
    id: reduxPost.id,
    title: reduxPost.title,
    slug: reduxPost.slug,
    author: reduxPost.author,
    content: reduxPost.content,
    status: reduxPost.status as 'draft' | 'published' | 'pending_review',
    createdAt: reduxPost.created_at ? new Date(reduxPost.created_at).toISOString() : new Date().toISOString()
  } : null;

  // Error handling for post fetching
  if (status === 'loading') {
    return <Preloader />;
  }

  if (status === 'failed') {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8 text-center">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Error Loading Post </strong>
          <span className="block sm:inline">Failed to load post</span>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8 text-center">
        <p className="text-gray-600">No post found</p>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{post.title} | TherapistDirect Blog</title>
        <meta name="description" content={post.content.substring(0, 160) + '...'} />
        <meta name="keywords" content="mental health, therapy, counseling, blog post, {post.title.toLowerCase()}" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.content.substring(0, 160) + '...'} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://therapistdirect.com/blog/${post.slug}`} />
        <meta property="article:published_time" content={post.createdAt} />
        <meta property="article:author" content="TherapistDirect Team" />
        <link rel="canonical" href={`https://therapistdirect.com/blog/${post.slug}`} />
        
        {/* Schema.org structured data for blog post */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": post.title,
            "description": post.content.substring(0, 160),
            "author": {
              "@type": "Person",
              "name": "TherapistDirect Team"
            },
            "publisher": {
              "@type": "Organization",
              "name": "TherapistDirect",
              "url": "https://therapistdirect.com"
            },
            "datePublished": post.createdAt,
            "dateModified": post.createdAt,
            "url": `https://therapistdirect.com/blog/${post.slug}`,
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": `https://therapistdirect.com/blog/${post.slug}`
            }
          })}
        </script>
      </Helmet>
      
      <article 
        className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 min-h-screen"
        aria-labelledby="blog-post-title"
      >
        <header>
        <h1 
          id="blog-post-title" 
          className="text-4xl font-extrabold text-gray-900 mb-4 leading-tight"
        >
          {post.title}
        </h1>

        <div 
          className="flex items-center space-x-4 text-gray-600 mb-8 border-b pb-4"
          aria-label="Post metadata"
        >
          <div className="flex items-center">
            <User className="h-5 w-5 mr-2 text-gray-500" />
            <span className="text-sm font-medium">Anonymous</span>
          </div>
          <div className="flex items-center">
            <Clock className="h-5 w-5 mr-2 text-gray-500" />
            <time dateTime={post.createdAt}>
              {post.createdAt
                ? format(new Date(post.createdAt), "MMMM d, yyyy")
                : "Date not available"}
            </time>
          </div>
        </div>
      </header>

      <div 
        className="prose prose-lg prose-blue max-w-none mb-8 editor-render"
        aria-label="Blog post content"
      >
        <MDXStringRenderer mdxString={post.content} />
      </div>
    </article>
    </>
  );
};

export default BlogPostPage;